import type { ThemeConfig } from "antd";
import { theme as antdTheme } from "antd";

export type ThemeMode = "light" | "dark";

/** Single source of truth for the antd theme; mirrors src/theme/tokens.css.
 *  OSI visual guidelines: navy shell, cream workspace, signal-gold accent,
 *  Montserrat / Poppins, 8/14/24 radii, pill primary actions. */
export const palette = {
  navy: "#001b33",
  navyDeep: "#04243d",
  panelDark: "#133752",
  steel: "#234e7b",
  gold: "#e2902a",
  goldHover: "#f0a93d",
  goldOnLight: "#885619",
  danger: "#b91c1c",
  warn: "#92400e",
  ok: "#047857",
  light: {
    bg: "#f2e9de",
    surface: "#fffaf4",
    sunken: "#e8ddcd",
    text: "#001b33",
    textMuted: "#576979",
    textFaint: "#4c6880",
    border: "rgba(0,27,51,0.16)",
  },
  dark: {
    bg: "#03223a",
    surface: "#0b2e4a",
    sunken: "#00162b",
    text: "#ffffff",
    textMuted: "#a9b6c2",
    textFaint: "#8a98a5",
    border: "rgba(255,255,255,0.18)",
  },
};

const FONT = '"Poppins", system-ui, -apple-system, "Segoe UI", sans-serif';
const FONT_HEADING = '"Montserrat", "Poppins", system-ui, sans-serif';
const EASE = "cubic-bezier(0.22, 0.61, 0.36, 1)";

export function buildTheme(mode: ThemeMode): ThemeConfig {
  const dark = mode === "dark";
  const p = dark ? palette.dark : palette.light;
  // gold-on-cream text must use the darker gold; bright gold only on navy
  const accent = dark ? palette.gold : palette.goldOnLight;
  const danger = dark ? "#ff8f8f" : palette.danger;
  const warn = dark ? "#f0b36a" : palette.warn;
  const ok = dark ? "#5fd1a3" : palette.ok;
  const selectedBg = dark ? palette.panelDark : "#e9eef4";
  const selectedHoverBg = dark ? "#1b4466" : "#d3dfeb";

  return {
    algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      // — color —
      colorPrimary: accent,
      colorInfo: dark ? "#9fbad4" : palette.steel,
      colorSuccess: ok,
      colorWarning: warn,
      colorError: danger,
      colorLink: accent,
      colorLinkHover: dark ? palette.goldHover : palette.navy,
      colorLinkActive: dark ? palette.gold : palette.navy,
      colorBgLayout: p.bg,
      colorBgContainer: dark ? p.surface : "#ffffff",
      colorBgElevated: p.surface,
      colorBorder: p.border,
      colorBorderSecondary: p.border,
      colorText: p.text,
      colorTextSecondary: p.textMuted,
      colorTextTertiary: p.textFaint,
      colorTextHeading: p.text,
      colorFillAlter: p.sunken,
      colorFillSecondary: p.sunken,

      // — geometry: guidelines §5 (8 / 14 / 24) —
      borderRadius: 8,
      borderRadiusLG: 24,
      borderRadiusSM: 8,
      borderRadiusXS: 4,
      wireframe: false,

      // — typography: Poppins body/UI; Montserrat headings via component tokens —
      fontFamily: FONT,
      fontFamilyCode: 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace',
      fontSize: 14,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeXL: 20,
      fontSizeHeading1: 40,
      fontSizeHeading2: 30,
      fontSizeHeading3: 24,
      fontSizeHeading4: 20,
      fontSizeHeading5: 16,
      lineHeight: 1.6,
      lineHeightHeading1: 1.1,
      lineHeightHeading2: 1.15,
      lineHeightHeading3: 1.2,

      // — density: 44px minimum touch targets, 48px for forms —
      controlHeight: 44,
      controlHeightSM: 32,
      controlHeightLG: 48,
      controlHeightXS: 24,
      padding: 16,
      paddingXS: 8,
      paddingSM: 12,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginXS: 8,
      marginSM: 12,
      marginLG: 24,
      sizeStep: 4,
      sizeUnit: 4,

      // — elevation: subtle; little or no resting shadow —
      boxShadow: dark ? "0 1px 2px rgba(0,0,0,.4)" : "0 1px 2px rgba(0,27,51,.08)",
      boxShadowSecondary: dark ? "0 8px 24px rgba(0,0,0,.4)" : "0 8px 24px rgba(0,27,51,.10)",
      boxShadowTertiary: "none",

      // — motion: guidelines §8 —
      motionDurationFast: "0.16s",
      motionDurationMid: "0.26s",
      motionDurationSlow: "0.36s",
      motionEaseInOut: EASE,
      motionEaseOut: EASE,
    },
    components: {
      Layout: {
        headerBg: p.surface,
        headerHeight: 56,
        headerPadding: "0 24px",
        bodyBg: p.bg,
        siderBg: "transparent",
        footerBg: p.bg,
      },
      Card: {
        headerBg: "transparent",
        headerHeight: 52,
        headerHeightSM: 44,
        headerFontSize: 16,
        headerFontSizeSM: 14,
        paddingLG: 24,
        boxShadowTertiary: "none",
        colorBorderSecondary: p.border,
        colorBgContainer: p.surface,
      },
      Table: {
        headerBg: p.sunken,
        headerColor: p.textMuted,
        headerSplitColor: "transparent",
        borderColor: p.border,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
        cellPaddingBlockSM: 8,
        cellPaddingInlineSM: 12,
        rowHoverBg: dark ? "#0f3555" : "#f7efe4",
        rowSelectedBg: selectedBg,
        rowSelectedHoverBg: selectedHoverBg,
        footerBg: p.sunken,
        fontWeightStrong: 600,
      },
      // primary: gold pill with navy label (the single restrained accent action)
      Button: {
        fontWeight: 600,
        primaryShadow: "none",
        defaultShadow: "none",
        dangerShadow: "none",
        paddingInline: 20,
        contentFontSize: 14,
        borderRadius: 999,
        borderRadiusLG: 999,
        borderRadiusSM: 999,
        defaultBorderColor: dark ? "rgba(255,255,255,.4)" : palette.navy,
        defaultColor: p.text,
        defaultBg: "transparent",
        colorPrimary: palette.gold,
        colorPrimaryHover: palette.goldHover,
        colorPrimaryActive: palette.gold,
        primaryColor: palette.navy,
        colorBgTextHover: dark ? "rgba(255,255,255,.08)" : "rgba(0,27,51,.06)",
      },
      Input: {
        paddingBlock: 10,
        paddingInline: 16,
        activeShadow: dark ? "0 0 0 3px rgba(226,144,42,.25)" : "0 0 0 3px rgba(35,78,123,.2)",
        activeBorderColor: dark ? palette.gold : palette.steel,
        hoverBorderColor: dark ? palette.gold : palette.steel,
        borderRadius: 12,
        borderRadiusLG: 12,
        borderRadiusSM: 8,
        colorBgContainer: dark ? "#0b2e4a" : "#ffffff",
      },
      InputNumber: { borderRadius: 12, borderRadiusLG: 12, activeBorderColor: dark ? palette.gold : palette.steel },
      DatePicker: { borderRadius: 12, borderRadiusLG: 12, activeBorderColor: dark ? palette.gold : palette.steel },
      Select: {
        borderRadius: 12,
        borderRadiusLG: 12,
        optionSelectedBg: selectedBg,
        optionHeight: 40,
        optionFontSize: 14,
      },
      Tabs: {
        horizontalMargin: "0 0 16px 0",
        horizontalItemPadding: "12px 0",
        horizontalItemGutter: 32,
        titleFontSize: 14,
        inkBarColor: dark ? palette.gold : palette.goldOnLight,
        itemColor: p.textMuted,
        itemSelectedColor: p.text,
        itemHoverColor: dark ? palette.goldHover : palette.goldOnLight,
        itemActiveColor: p.text,
      },
      // Menu lives on the navy sider: white text, gold selected indicator
      Menu: {
        itemHeight: 44,
        itemMarginInline: 0,
        itemMarginBlock: 2,
        itemPaddingInline: 12,
        iconMarginInlineEnd: 12,
        iconSize: 16,
        collapsedIconSize: 18,
        itemBorderRadius: 8,
        darkItemBg: "transparent",
        darkSubMenuItemBg: "transparent",
        darkItemColor: "rgba(255,255,255,.78)",
        darkItemHoverBg: "rgba(255,255,255,.08)",
        darkItemHoverColor: "#ffffff",
        darkItemSelectedBg: "rgba(226,144,42,.16)",
        darkItemSelectedColor: "#ffffff",
        activeBarBorderWidth: 0,
      },
      Statistic: { titleFontSize: 12, contentFontSize: 28 },
      Tag: { defaultBg: p.sunken, defaultColor: p.textMuted, lineHeightSM: 1.6, borderRadiusSM: 8 },
      Alert: { borderRadiusLG: 14 },
      Descriptions: { labelBg: "transparent", itemPaddingBottom: 12, colonMarginRight: 12 },
      Modal: {
        titleFontSize: 20,
        headerBg: p.surface,
        contentBg: p.surface,
        paddingContentHorizontalLG: 24,
        borderRadiusLG: 24,
        fontFamily: FONT_HEADING,
      },
      Typography: { fontFamily: FONT, titleMarginBottom: "0.5em" },
      Segmented: { itemSelectedBg: p.surface, borderRadiusSM: 8 },
      Empty: { colorTextDescription: p.textMuted },
      Tooltip: { colorBgSpotlight: palette.navy, borderRadius: 8 },
      Divider: { colorSplit: p.border },
      Form: { labelFontSize: 14, labelColor: p.text, itemMarginBottom: 20, verticalLabelPadding: "0 0 6px" },
    },
  };
}
